import React from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@radix-ui/react-dropdown-menu";

import { primaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import "../../components/DropdownMenu.css";

// Two-line rows (label + hint), so these can't use DropdownMenu.css's
// .DropdownMenuItem — that's a fixed 40px single-line row. The shared
// .DropdownMenuContent / .DropdownMenuSeparator classes still apply.
const ITEM_CLS =
  "flex flex-col items-start gap-0.5 px-3 py-2 rounded-ctrl outline-none cursor-pointer select-none data-[highlighted]:bg-surface-2";

function MenuItem({ label, hint, onSelect }) {
  return (
    <DropdownMenuItem className={ITEM_CLS} onSelect={onSelect}>
      <span className="text-[13px] font-medium text-text">{label}</span>
      <span className="text-[11px] text-text-2">{hint}</span>
    </DropdownMenuItem>
  );
}
MenuItem.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

// The Roles & Access page's one primary action. Templates stopped being a tab
// (they grant no access and can't be assigned, so they never belonged beside
// the roles that do) — this menu is where they surface instead, at the moment
// an admin is actually choosing a starting point.
export default function CreateRoleMenu({ canManage, onPickRecommended, onPickTemplate, onCustom, onStarterSet }) {
  // No disabled state: a read-only admin gets no create affordance at all
  // rather than one that looks available and isn't.
  if (!canManage) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={primaryButtonCls}>
          <FontAwesomeIcon icon={icon({ name: "plus", style: "regular" })} className="mr-2 text-xs" />
          Create role
          <FontAwesomeIcon
            icon={icon({ name: "angle-down", family: "sharp", style: "solid" })}
            className="ml-2 text-xs"
          />
        </button>
      </DropdownMenuTrigger>
      {/* Portal to <body> so the menu escapes the TopBar's sticky stacking
          context and paints above the page header. */}
      <DropdownMenuPortal>
        <DropdownMenuContent className="DropdownMenuContent w-[320px]" side="bottom" align="end" sideOffset={6}>
          <MenuItem
            label="Start from a recommended role"
            hint="the four roles most finance setups need"
            onSelect={onPickRecommended}
          />
          <MenuItem
            label="Start from another template"
            hint="browse all managed templates"
            onSelect={onPickTemplate}
          />
          <MenuItem
            label="Create a custom role"
            hint="start from scratch, pick capabilities yourself"
            onSelect={onCustom}
          />
          <DropdownMenuSeparator className="DropdownMenuSeparator" />
          <MenuItem
            label="Create the recommended finance role set"
            hint="drafts the whole set at once"
            onSelect={onStarterSet}
          />
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

CreateRoleMenu.propTypes = {
  canManage: PropTypes.bool,
  onPickRecommended: PropTypes.func.isRequired,
  onPickTemplate: PropTypes.func.isRequired,
  onCustom: PropTypes.func.isRequired,
  onStarterSet: PropTypes.func.isRequired,
};
